import { PhotoIcon, PlusIcon, SmallPenIcon, TrashIcon } from "assets/icons";
import cn from "classnames";
import { MAX_IMAGE_FILE_SIZE } from "constants/common";
import { MediaType } from "constants/enums";
import { handleUploadFileError } from "helpers";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useDirectUploadToS3 } from "libs/directUpload";
import isEmpty from "lodash/isEmpty";
import { FileRejection, FileWithPath, useDropzone } from "react-dropzone";
import { ClipLoader } from "react-spinners";
import { Error, Icon } from "ui";

import { UploadImageProps } from "./UploadImage.types";

import styles from "./UploadImage.module.scss";

const UPLOAD_ZONE_ICON = {
  primary: <Icon as={PhotoIcon} boxSize={30} color="#A8A8BB" />,
  secondary: <Icon as={PlusIcon} boxSize={24} color="#FB3973" />
};

const UploadImage = ({
  className,
  variant = "primary",
  uploadLabel = "Change photo",
  editLabel = "Change photo",
  deleteLabel = "Delete photo",
  value,
  onChange,
  uploadDirectoryKey,
  accept = [".jpeg", ".jpg", ".png", ".webp"],
  errorMessage,
  ...props
}: UploadImageProps) => {
  const { uploader, isLoading } = useDirectUploadToS3();

  const onDropImage = (files: FileWithPath[]) => {
    if (!isEmpty(files)) {
      uploader(files[0], {
        directoryKey: uploadDirectoryKey,
        onSuccess: onChange
      });
    }
  };

  const onDropImageRejected = (files: FileRejection[]) => {
    const file = files[0];
    const message = file.errors[0].message;
    const code = file.errors[0].code;

    handleUploadFileError(message, code, [MediaType.IMAGE]);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      "image/*": accept
    },
    maxSize: MAX_IMAGE_FILE_SIZE,
    maxFiles: 1,
    noDrag: true,
    noKeyboard: true,
    onDrop: onDropImage,
    onDropRejected: onDropImageRejected
  });

  return (
    <div className={cn(styles["upload-image"], className)} {...props}>
      <div
        {...getRootProps()}
        className={cn(
          styles["upload-image__zone"],
          styles[`upload-image__zone-${variant}`]
        )}
      >
        <input {...getInputProps()} style={{ display: "none" }} />
        {value ? (
          <img className="default-image" src={value.url} alt="img" />
        ) : isLoading ? (
          <div className={styles["upload-image__loader"]}>
            <ClipLoader size={30} color="#FB3973" />
          </div>
        ) : (
          UPLOAD_ZONE_ICON[variant]
        )}
      </div>
      <div className={styles["upload-image__control"]}>
        {value ? (
          <>
            <div
              className={styles["upload-image__control--button"]}
              onClick={open}
            >
              <Icon as={SmallPenIcon} boxSize={15} color="#595961" />
              {t(editLabel)}
            </div>
            <div
              className={cn(
                styles["upload-image__control--button"],
                styles["upload-image__control--button-delete"]
              )}
              onClick={() => onChange(null)}
            >
              <Icon as={TrashIcon} boxSize={14} color="#ff0033" />
              {t(deleteLabel)}
            </div>
          </>
        ) : (
          <div
            className={styles["upload-image__control--button"]}
            onClick={open}
          >
            {t(uploadLabel)}
          </div>
        )}
      </div>
      <Error message={errorMessage} />
    </div>
  );
};

export default UploadImage;
